import React from "react";
import clsx from "clsx";
import "./sticker.styles.scss";

export interface StickerProps {
	className?: string;

	size: number;
	autoPlay?: boolean;
	loop?: boolean;
	iterations?: number;

	data: any;
}

const BlockName = "sticker";
const ClassNames = {
	Block: BlockName,
	Modifiers: {
		StickerSize: "--sticker-size",
		Static: "--static",
		SpriteAnimated: "--sprite-animated",
	},
	Elements: {
		SpriteSheet: `${BlockName}__sprite-sheet`,
	},
} as const;

export type StickerComponent = React.FC<StickerProps> & {
	ClassNames: typeof ClassNames;
};

const SpriteAnimationId = "sprite-anim";
const animateSprite = (
	spriteSheet: HTMLDivElement,
	size: number,
	totalFrames: number,
	duration: number,
	loop: boolean,
	iterations: number,
) => {
	const keyframes = [
		{
			transform: "translateX(0)",
		},
		{
			transform: `translateX(-${size * totalFrames}px)`,
		},
	];
	const spriteAnim = spriteSheet.animate(keyframes, {
		id: SpriteAnimationId,
		duration: duration * (totalFrames - 1),
		iterations: loop ? Infinity : iterations,
		easing: `steps(${totalFrames})`,
	});
	spriteAnim.onfinish = () => {
		spriteAnim.cancel();
	};
	return spriteAnim;
};

export const Sticker = ((props: StickerProps) => {
	const spriteSheetRef = React.useRef<HTMLDivElement>(null);

	const { size = 130, autoPlay = true, loop = false, iterations = 3, data } = props;
	const totalFrames = data.totalFrames || 1;
	const duration = data.duration || 0;
	const isSprite = totalFrames > 1;
	const isStatic = !isSprite;

	React.useEffect(() => {
		const spriteSheet = spriteSheetRef.current;
		if (!spriteSheet) return;

		if (isSprite && autoPlay) {
			animateSprite(spriteSheet, size, totalFrames, duration, loop, iterations);
			return () => {
				spriteSheet.getAnimations().forEach((a) => a.cancel());
			};
		}
	}, []);

	const handleMouseEnterSticker = () => {
		if (isSprite) {
			const spriteSheet = spriteSheetRef.current;
			if (!spriteSheet) return;
			const spriteAnim = spriteSheet.getAnimations().find((a) => a.id === SpriteAnimationId);
			if (spriteAnim) {
				//NOTE: It means the animation is playing, so we don't need to play it again.
			} else {
				animateSprite(spriteSheet, size, totalFrames, duration, loop, iterations);
			}
		}
	};

	return (
		<div
			className={clsx(
				Sticker.ClassNames.Block,
				{
					[Sticker.ClassNames.Modifiers.Static]: isStatic,
					[Sticker.ClassNames.Modifiers.SpriteAnimated]: isSprite,
				},
				props.className,
			)}
			style={{
				//@ts-ignore
				[Sticker.ClassNames.Modifiers.StickerSize]: `${size}px`,
			}}
			onMouseEnter={handleMouseEnterSticker}
		>
			<div
				ref={spriteSheetRef}
				className={Sticker.ClassNames.Elements.SpriteSheet}
				style={{
					backgroundImage: `url(${data.stickerSpriteUrl})`,
					backgroundRepeat: "no-repeat",
					backgroundSize: `${totalFrames * size}px ${size}px`,
					width: `${totalFrames * size}px`,
					height: `${size}px`,
				}}
			/>
		</div>
	);
}) as StickerComponent;
Sticker.ClassNames = ClassNames;
